import Decimal from 'decimal.js'
import pino from 'pino'

import { StateError } from '@ankor-io/common/lang/backend/errors'

const logger = pino({ name: 'currency' })

/**
 * Convert a currency amount from one currency to another
 * @param amountCents - The amount in cents
 * @param rate - The conversion rate
 * @returns The converted amount in cents
 */
export const convert = (amountCents: number, rate: string): number => {
  // we need a robust implementation of this function, make sure the amount is a non-negative integer
  if (!Number.isInteger(amountCents)) {
    throw new Error('Amount must be an integer in cents')
  }
  // if the rate is not provided or not a number, then throw an error
  if (!rate || isNaN(parseFloat(rate))) {
    throw new Error('Rate must be a valid numeric string')
  }

  try {
    // Convert cents to decimal amount
    const amountDecimal = new Decimal(amountCents).div(100)

    // Perform conversion
    const convertedDecimal = amountDecimal.mul(rate)

    // Convert back to cents and round
    const convertedCents = convertedDecimal.mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP)

    const result = convertedCents.toNumber()

    // Check for overflow/underflow
    if (!Number.isFinite(result) || result > Number.MAX_SAFE_INTEGER) {
      throw new Error('Conversion result exceeds safe integer range')
    }

    return result
  } catch (error) {
    logger.error(error, 'Currency conversion failed')
    throw StateError('Currency conversion failed', { error })
  }
}

/**
 * Calculates VAT amount from a base amount
 * @param amountCents - Base amount in cents (integer)
 * @param vatPercentage - VAT percentage as string (e.g., "20", "7.5", "0")
 * @returns VAT amount in cents (integer)
 */
export const calculateVat = (amountCents: number, vatPercentage: string): number => {
  // Input validation
  if (!Number.isInteger(amountCents)) {
    throw new Error('Amount must be an integer in cents')
  }

  const vatPercent = new Decimal(vatPercentage)
  if (vatPercent.lt(0) || vatPercent.gt(100)) {
    throw new Error('VAT percentage must be between 0 and 100')
  }

  try {
    // Convert cents to decimal amount
    const amountDecimal = new Decimal(amountCents).div(100)

    // Calculate VAT: amount * (vatPercentage / 100)
    const vatAmount = amountDecimal.mul(vatPercent).div(100)

    // Convert back to cents and round
    const vatCents = vatAmount.mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP)

    return vatCents.toNumber()
  } catch (error) {
    logger.error(error, 'VAT calculation failed')
    throw StateError('VAT calculation failed', { error })
  }
}

export const sumAll = (amounts: bigint[]): bigint => {
  // this is fine because the amounts are all in cents
  return amounts.reduce((acc, amount) => acc + amount, BigInt(0))
}

export const toCents = (amount: number): number => {
  if (isNaN(amount)) {
    throw new Error('Invalid amount')
  }
  return new Decimal(amount).mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber()
}

export const fromCents = (cents: number): number => {
  if (isNaN(cents)) {
    throw new Error('Invalid cents amount')
  }
  return new Decimal(cents).div(100).toNumber()
}

export const toNegative = (value: number) => new Decimal(value).abs().neg().toNumber()

export const toPositive = (value: number) => new Decimal(value).abs().toNumber()

/**
 * APA remaining reconciliation: deposited - abs(spend) - abs(payable VAT).
 * Single home for the formula; charter detail stats and list enrichment must both
 * compute remaining through here so the two views cannot drift.
 * Inputs are signed sums in cents (sum first, then abs here).
 */
export const calculateApaRemainingCents = (args: {
  depositedCents: number
  spentCents: number
  payableVatCents: number
}): number => {
  return args.depositedCents - toPositive(args.spentCents) - toPositive(args.payableVatCents)
}

export const NUMBER = {
  /**
   * Converts a string to a number using BigDecimal precision
   * @param value - The string value to convert
   * @returns The converted number
   * @throws Error if the string is not a valid number
   */
  fromText: (value: string): number => {
    if (!value || typeof value !== 'string') {
      throw new Error('Value must be a non-empty string')
    }

    // Trim whitespace
    const trimmedValue = value.trim()

    if (trimmedValue === '') {
      throw new Error('Value cannot be an empty string')
    }

    try {
      const decimal = new Decimal(trimmedValue)

      // Check if the decimal is valid
      if (!decimal.isFinite()) {
        throw new Error('Value must be a finite number')
      }

      return decimal.toNumber()
    } catch (error) {
      logger.error(error, 'BigDecimal conversion failed', { value: trimmedValue })
      throw StateError('BigDecimal conversion failed', { error, value: trimmedValue })
    }
  },
}

/**
 * Calculates a percentage of an amount safely for financial operations
 * @param amountCents - Base amount in cents (integer)
 * @param percentage - Percentage as a number (e.g., 20 for 20%, 7.5 for 7.5%)
 * @returns Percentage amount in cents (integer)
 */
export const percentageOf = (amountCents: number, percentage: number): number => {
  // Input validation
  if (!Number.isInteger(amountCents)) {
    throw new Error('Amount must be an integer in cents')
  }

  if (isNaN(percentage) || !Number.isFinite(percentage)) {
    throw new Error('Percentage must be a valid finite number')
  }

  const percentageDecimal = new Decimal(percentage)
  if (percentageDecimal.lt(0)) {
    throw new Error('Percentage must be non-negative')
  }

  try {
    // Convert cents to decimal amount
    const amountDecimal = new Decimal(amountCents).div(100)

    // Calculate percentage: amount * (percentage / 100)
    const percentageAmount = amountDecimal.mul(percentageDecimal).div(100)

    // Convert back to cents and round
    const percentageCents = percentageAmount.mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP)

    const result = percentageCents.toNumber()

    // Check for overflow/underflow
    if (!Number.isFinite(result) || result > Number.MAX_SAFE_INTEGER) {
      throw new Error('Percentage calculation result exceeds safe integer range')
    }

    return result
  } catch (error) {
    logger.error(error, 'Percentage calculation failed')
    throw StateError('Percentage calculation failed', { error })
  }
}

/**
 * Multiply an amount by an FX rate safely using BigDecimal
 * @param amount - The amount as a number
 * @param rate - The FX rate as a string or number
 * @returns The result as a number
 */
export const multiplyByRate = (amount: number, rate: string | number): number => {
  // Input validation
  if (typeof amount !== 'number' || isNaN(amount) || !Number.isFinite(amount)) {
    throw new Error('Amount must be a valid finite number')
  }

  if (amount < 0) {
    throw new Error('Amount must be non-negative')
  }

  // Validate rate
  const rateStr = typeof rate === 'number' ? rate.toString() : rate
  if (!rateStr || isNaN(parseFloat(rateStr))) {
    throw new Error('Rate must be a valid numeric string or number')
  }

  try {
    const rateDecimal = new Decimal(rateStr)

    // Check for valid rate
    if (!rateDecimal.isFinite() || rateDecimal.isNegative()) {
      throw new Error('Rate must be a finite, non-negative number')
    }

    // Multiply amount by rate
    const amountDecimal = new Decimal(amount)
    const resultDecimal = amountDecimal.mul(rateDecimal)

    const result = resultDecimal.toNumber()

    // Check for overflow/underflow
    if (!Number.isFinite(result)) {
      throw new Error('Multiplication result is not finite')
    }

    return result
  } catch (error) {
    logger.error(error, 'FX rate multiplication failed', { amount, rate })
    throw StateError('FX rate multiplication failed', { error, amount, rate })
  }
}

/**
 * Multiply a signed amount by an FX rate. Converts the absolute value through
 * `multiplyByRate` and re-applies the sign. Use this when the amount can
 * legitimately be negative (e.g. converting cash float balances that may be
 * overdrawn). For inputs that should never be negative, prefer `multiplyByRate`.
 */
export const multiplyByRateSigned = (amount: number, rate: string | number): number => {
  const sign = amount < 0 ? -1 : 1
  return sign * multiplyByRate(Math.abs(amount), rate)
}

/**
 * Safely sum two numbers using BigDecimal
 * @param a - The first number
 * @param b - The second number
 * @returns The sum as a number
 */
export const sum = (a: number, b: number): number => {
  // Input validation
  if (typeof a !== 'number' || isNaN(a) || !Number.isFinite(a)) {
    throw new Error('First number must be a valid finite number')
  }

  if (typeof b !== 'number' || isNaN(b) || !Number.isFinite(b)) {
    throw new Error('Second number must be a valid finite number')
  }

  try {
    const aDecimal = new Decimal(a)
    const bDecimal = new Decimal(b)
    const resultDecimal = aDecimal.plus(bDecimal)

    const result = resultDecimal.toNumber()

    // Check for overflow/underflow
    if (!Number.isFinite(result)) {
      throw new Error('Sum result is not finite')
    }

    return result
  } catch (error) {
    logger.error(error, 'Sum calculation failed', { a, b })
    throw StateError('Sum calculation failed', { error, a, b })
  }
}

/**
 * Calculate the reciprocal of a rate using BigDecimal (1 / rate)
 * This is useful for reversing FX rates (e.g., if you have USD->EUR, get EUR->USD)
 * @param rate - The rate as a string or number
 * @returns The reciprocal rate as a string to preserve precision
 */
export const reciprocalRate = (rate: string | number): string => {
  // Validate rate input
  const rateStr = typeof rate === 'number' ? rate.toString() : rate
  if (!rateStr || isNaN(parseFloat(rateStr))) {
    throw new Error('Rate must be a valid numeric string or number')
  }

  try {
    const rateDecimal = new Decimal(rateStr)

    // Check for valid rate
    if (!rateDecimal.isFinite()) {
      throw new Error('Rate must be a finite number')
    }

    // Check for zero rate (would cause division by zero)
    if (rateDecimal.isZero()) {
      throw new Error('Rate cannot be zero')
    }

    // Check for negative rate
    if (rateDecimal.isNegative()) {
      throw new Error('Rate must be positive')
    }

    // Calculate reciprocal: 1 / rate
    const reciprocal = new Decimal(1).div(rateDecimal)

    // Return as string to preserve precision
    return reciprocal.toString()
  } catch (error) {
    logger.error(error, 'Reciprocal rate calculation failed', { rate })
    throw StateError('Reciprocal rate calculation failed', { error, rate })
  }
}
